import React, { useState, useEffect, useCallback } from 'react';
import '../../assets/css/components/onboarding_tour.css';

const OnboardingTour = ({ onComplete, userType = 'farmer' }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);

    // Different tour content based on user type
    const tourSteps = {
        farmer: [
            {
                icon: '👋',
                title: 'Welcome to E-AquaHealth',
                description: 'Your all-in-one platform for aquaculture health management. Let\'s show you around!',
                illustration: 'welcome',
                tip: 'This tour takes about 2 minutes'
            },
            {
                icon: '🏠',
                title: 'Your Dashboard',
                description: 'Get a quick overview of your farms, active cases, and important notifications all in one place.',
                illustration: 'dashboard',
                tip: 'Access your dashboard anytime from the sidebar'
            },
            {
                icon: '🐟',
                title: 'Manage Your Farms',
                description: 'Add and manage multiple fish farms. Track pond details, fish species, and health metrics.',
                illustration: 'farms',
                tip: 'Keep your farm details updated for better recommendations'
            },
            {
                icon: '🩺',
                title: 'Report Health Issues',
                description: 'Noticed something wrong? Create a case report with photos and symptoms. Our vets will respond quickly.',
                illustration: 'cases',
                tip: 'Include clear photos for faster diagnosis'
            },
            {
                icon: '👨‍⚕️',
                title: 'Connect with Experts',
                description: 'Access qualified aquatic veterinarians and laboratories for professional consultations and tests.',
                illustration: 'experts',
                tip: 'You can chat directly with assigned vets'
            },
            {
                icon: '🛒',
                title: 'Shop Quality Products',
                description: 'Browse and order medications, feeds, and equipment from verified vendors.',
                illustration: 'shop',
                tip: 'Check vendor ratings before ordering'
            },
            {
                icon: '✅',
                title: 'You\'re All Set!',
                description: 'Start by adding your first farm or exploring available veterinarians.',
                illustration: 'complete',
                tip: 'Need help? Check the FAQ or contact support'
            }
        ],
        vet: [
            {
                icon: '👋',
                title: 'Welcome, Doctor!',
                description: 'E-AquaHealth connects you with fish farmers who need your expertise.',
                illustration: 'welcome',
                tip: 'Complete your profile to start receiving cases'
            },
            {
                icon: '📋',
                title: 'Case Management',
                description: 'View and respond to cases assigned to you. Communicate directly with farmers.',
                illustration: 'cases',
                tip: 'Timely responses improve your rating'
            },
            {
                icon: '📊',
                title: 'Track Your Performance',
                description: 'Monitor your consultations, ratings, and earnings from the dashboard.',
                illustration: 'dashboard',
                tip: 'Higher ratings mean more case assignments'
            },
            {
                icon: '✅',
                title: 'Ready to Help!',
                description: 'Start accepting cases and make a difference in aquaculture health.',
                illustration: 'complete',
                tip: 'Update your availability in settings'
            }
        ],
        lab: [
            {
                icon: '👋',
                title: 'Welcome to E-AquaHealth',
                description: 'Manage laboratory tests and deliver results efficiently.',
                illustration: 'welcome',
                tip: 'Complete your lab profile to receive samples'
            },
            {
                icon: '🔬',
                title: 'Manage Samples',
                description: 'Track incoming samples, process tests, and upload results seamlessly.',
                illustration: 'samples',
                tip: 'Quick turnaround improves your lab rating'
            },
            {
                icon: '📊',
                title: 'Analytics Dashboard',
                description: 'View test volumes, revenue, and performance metrics.',
                illustration: 'dashboard',
                tip: 'Monitor trends to optimize operations'
            },
            {
                icon: '✅',
                title: 'Start Processing!',
                description: 'You\'re ready to receive and process sample requests.',
                illustration: 'complete',
                tip: 'Keep your test menu updated'
            }
        ],
        vendor: [
            {
                icon: '👋',
                title: 'Welcome, Vendor!',
                description: 'Reach thousands of fish farmers with your products.',
                illustration: 'welcome',
                tip: 'A complete store profile attracts more buyers'
            },
            {
                icon: '📦',
                title: 'Manage Products',
                description: 'Add products, set prices, and manage inventory with ease.',
                illustration: 'products',
                tip: 'Good photos and descriptions boost sales'
            },
            {
                icon: '🛒',
                title: 'Process Orders',
                description: 'Track orders, manage shipments, and communicate with buyers.',
                illustration: 'orders',
                tip: 'Fast shipping improves your store rating'
            },
            {
                icon: '✅',
                title: 'Start Selling!',
                description: 'Add your first product and start reaching customers.',
                illustration: 'complete',
                tip: 'Competitive pricing attracts more orders'
            }
        ],
        admin: [
            {
                icon: '👋',
                title: 'Welcome, Administrator!',
                description: 'You have full control over the E-AquaHealth platform.',
                illustration: 'welcome',
                tip: 'Great power comes with great responsibility'
            },
            {
                icon: '👥',
                title: 'User Management',
                description: 'Manage users, approve professionals, and handle account issues.',
                illustration: 'users',
                tip: 'Review pending verifications regularly'
            },
            {
                icon: '📊',
                title: 'Platform Analytics',
                description: 'Monitor platform health, user activity, and key metrics.',
                illustration: 'dashboard',
                tip: 'Watch for unusual activity patterns'
            },
            {
                icon: '⚙️',
                title: 'System Settings',
                description: 'Configure platform settings, fees, and operational parameters.',
                illustration: 'settings',
                tip: 'Test changes in staging first'
            },
            {
                icon: '✅',
                title: 'Ready to Manage!',
                description: 'You have access to all administrative functions.',
                illustration: 'complete',
                tip: 'Check the audit log for important changes'
            }
        ]
    };

    const steps = tourSteps[userType] || tourSteps.farmer;

    const handleNext = () => {
        if (currentStep < steps.length - 1) {
            setIsAnimating(true);
            setTimeout(() => {
                setCurrentStep(prev => prev + 1);
                setIsAnimating(false);
            }, 300);
        } else {
            handleComplete();
        }
    };

    const handlePrev = () => {
        if (currentStep > 0) {
            setIsAnimating(true);
            setTimeout(() => {
                setCurrentStep(prev => prev - 1);
                setIsAnimating(false);
            }, 300);
        }
    };

    const handleSkip = () => {
        handleComplete();
    };

    const handleComplete = () => {
        // Store completion per user type so different roles see their own tour
        const storageKey = `onboardingCompleted_${userType}`;
        localStorage.setItem(storageKey, 'true');
        localStorage.setItem(`onboardingCompletedAt_${userType}`, new Date().toISOString());
        onComplete?.();
    };

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'ArrowRight') handleNext();
        if (e.key === 'ArrowLeft') handlePrev();
        if (e.key === 'Escape') handleSkip();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep, steps.length]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    const currentStepData = steps[currentStep];
    const progress = ((currentStep + 1) / steps.length) * 100;

    return (
        <div className="onboarding-overlay">
            <div className="onboarding-modal">
                {/* Progress bar */}
                <div className="onboarding-progress">
                    <div 
                        className="progress-fill" 
                        style={{ width: `${progress}%` }}
                    />
                </div>

                {/* Skip button */}
                <button className="skip-btn" onClick={handleSkip}>
                    Skip tour
                </button>

                {/* Content */}
                <div className={`onboarding-content ${isAnimating ? 'animating' : ''}`}>
                    {/* Illustration */}
                    <div className={`illustration-container ${currentStepData.illustration}`}>
                        <div className="illustration-icon">
                            {currentStepData.icon}
                        </div>
                        <div className="illustration-rings">
                            <div className="ring ring-1"></div>
                            <div className="ring ring-2"></div>
                            <div className="ring ring-3"></div>
                        </div>
                    </div>

                    {/* Text content */}
                    <div className="text-content">
                        <h2 className="step-title">{currentStepData.title}</h2>
                        <p className="step-description">{currentStepData.description}</p>
                        
                        {/* Tip box */}
                        <div className="tip-box">
                            <span className="tip-icon">💡</span>
                            <span className="tip-text">{currentStepData.tip}</span>
                        </div>
                    </div>
                </div>

                {/* Step indicators */}
                <div className="step-indicators">
                    {steps.map((_, index) => (
                        <button
                            key={index}
                            className={`indicator ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                            onClick={() => {
                                setIsAnimating(true);
                                setTimeout(() => {
                                    setCurrentStep(index);
                                    setIsAnimating(false);
                                }, 300);
                            }}
                            aria-label={`Go to step ${index + 1}`}
                        />
                    ))}
                </div>

                {/* Navigation buttons */}
                <div className="onboarding-navigation">
                    <button 
                        className="nav-btn prev-btn"
                        onClick={handlePrev}
                        disabled={currentStep === 0}
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M15 18l-6-6 6-6" />
                        </svg>
                        Back
                    </button>

                    <span className="step-counter">
                        {currentStep + 1} of {steps.length}
                    </span>

                    <button 
                        className="nav-btn next-btn"
                        onClick={handleNext}
                    >
                        {currentStep === steps.length - 1 ? 'Get Started' : 'Next'}
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 18l6-6-6-6" />
                        </svg>
                    </button>
                </div>

                {/* Keyboard hints */}
                <div className="keyboard-hints">
                    <span>Use <kbd>←</kbd> <kbd>→</kbd> to navigate</span>
                </div>
            </div>
        </div>
    );
};

export default OnboardingTour;
